import React from 'react';
import {
  Box, Flex, keyframes, useColorModeValue,
} from '@chakra-ui/react';
import { useAppearance } from '../theme/AppearanceProvider';

// gentle, alive glow that breathes — the "web3" touch
const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 14px -5px var(--chakra-colors-brandGreen-400); }
  50%      { box-shadow: 0 0 22px -2px var(--chakra-colors-brandGreen-400); }
`;

/**
 * The mobile menu trigger (Aurora skin): a frosted-glass button that breathes a
 * soft accent glow, with accent-gradient bars (ragged middle) that snap to full
 * width and the whole button presses in on tap.
 */
const AuroraMenuButton: React.FC<{ onClick: () => void } & Record<string, any>> = ({
  onClick, ...rest
}) => {
  // item 406 — chip-tier glass (panels use useGlassPanel; this stays lighter-blur
  // chip material) but the edge weight matches the panels' calmer hairline
  const bg = useColorModeValue('rgba(255,255,255,0.6)', 'rgba(255,255,255,0.06)');
  const border = useColorModeValue('blackAlpha.300', 'whiteAlpha.200');
  // item 1513 — DECORATION is gated by the skin (CLAUDE.md law): the Accountant skin gets a
  // plain solid button — no frosted blur, no breathing glow, no gradient bars. The glass was
  // unconditional, so the flat skin's phone header wore Aurora's blur chip.
  const { aurora } = useAppearance();

  return (
    <Box
      as="button"
      type="button"
      aria-label="open menu"
      onClick={onClick}
      role="group"
      w="46px"
      h="46px"
      display="flex"
      alignItems="center"
      justifyContent="center"
      borderRadius="xl"
      bg={aurora ? bg : 'ledgerSurface'}
      borderWidth="1px"
      borderColor={aurora ? border : 'ledgerStroke'}
      sx={{
        ...(aurora ? { backdropFilter: 'var(--bx-glass-chip)' } : {}),
        '&:focus': { outline: 'none' },
        '&:focus-visible': { boxShadow: 'outline' },
      }}
      animation={aurora ? `${glowPulse} 3.2s ease-in-out infinite` : undefined}
      transition="transform 0.2s cubic-bezier(0.22, 1, 0.36, 1), border-color 0.2s"
      _hover={aurora ? { borderColor: 'brandGreen.400' } : { bg: 'ledgerSurfaceAlt' }}
      _active={{ transform: 'scale(0.9)' }}
      _focusVisible={aurora ? { outline: 'none', borderColor: 'brandGreen.400' } : { outline: 'none' }}
      {...rest}
    >
      <Flex direction="column" align="flex-start" justify="center" w="20px" gap="4px">
        {['top', 'mid', 'bot'].map((id) => (
          <Box
            key={id}
            h="2.5px"
            w={id === 'mid' ? '60%' : '100%'}
            borderRadius="full"
            bg={aurora ? undefined : 'ledgerInk'}
            bgGradient={aurora ? 'linear(to-r, brandGreen.300, brandGreen.500)' : undefined}
            boxShadow={aurora ? '0 0 8px -1px var(--chakra-colors-brandGreen-400)' : 'none'}
            transition="width 0.3s cubic-bezier(0.22, 1, 0.36, 1)"
            _groupHover={{ w: '100%' }}
            _groupActive={{ w: '100%' }}
          />
        ))}
      </Flex>
    </Box>
  );
};

export default AuroraMenuButton;
